
import { Network, Globe, Database, Users, Target, ArrowUp } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const MetaRepository = () => {
  const { t } = useTranslation('meta_repository');

  const features = [
    {
      title: t('why.features.silos.title'),
      description: t('why.features.silos.description'),
      icon: <Network className="w-6 h-6 text-brand-orange" />
    },
    {
      title: t('why.features.mobility.title'),
      description: t('why.features.mobility.description'),
      icon: <Users className="w-6 h-6 text-brand-orange" />
    },
    {
      title: t('why.features.alignment.title'),
      description: t('why.features.alignment.description'),
      icon: <Target className="w-6 h-6 text-brand-orange" />
    },
    {
      title: t('why.features.anticipation.title'),
      description: t('why.features.anticipation.description'),
      icon: <ArrowUp className="w-6 h-6 text-brand-orange" />
    }
  ];

  const goals = t('international.goals', { returnObjects: true });
  const goalsArray = Array.isArray(goals) ? goals : [];

  return (
    <div className="bg-background">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(45rem_50rem_at_top,theme(colors.brand.orange/8),transparent)] dark:bg-[radial-gradient(45rem_50rem_at_top,theme(colors.brand.orange/4),transparent)]" />
          <div className="absolute inset-0 bg-[radial-gradient(45rem_50rem_at_top_right,theme(colors.brand.blue/5),transparent)] dark:bg-[radial-gradient(45rem_50rem_at_top_right,theme(colors.brand.blue/3),transparent)]" />
        </div>
        
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-blue/10 dark:bg-brand-blue/5 text-brand-blue dark:text-brand-orange mb-8">
              <Globe className="w-5 h-5" />
              <span className="text-sm font-medium">{t('badge')}</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange leading-[1.2] pb-4">
              {t('title')}
            </h1>
            
            <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto">
              {t('description')}
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-24 bg-card">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange">
              {t('intro.title')}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t('intro.description')}
            </p>
          </div>
        </div>
      </section>

      {/* What is it Section */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange">
              {t('what.title')}
            </h2>
            <div className="bg-card rounded-2xl p-8 border border-border">
              <div className="flex items-start gap-6">
                <div className="p-3 bg-brand-orange/10 dark:bg-brand-orange/5 rounded-xl">
                  <Database className="w-8 h-8 text-brand-orange" />
                </div>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {t('what.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section className="py-24 bg-card">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-16 text-center bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange">
              {t('why.title')}
            </h2>
            <div className="grid sm:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="group bg-background rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-border hover:-translate-y-1"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 bg-brand-orange/10 dark:bg-brand-orange/5 rounded-xl group-hover:scale-110 transition-transform">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-medium text-brand-blue dark:text-brand-orange group-hover:text-[#8B5CF6] transition-colors">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange">
              {t('benefits.title')}
            </h2>
            <div className="space-y-6">
              {Object.values(t('benefits.items', { returnObjects: true })).map((benefit, index) => (
                <div key={index} className="bg-card rounded-xl p-6 border border-border">
                  <p className="text-lg text-muted-foreground">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* International Vision Section */}
      <section className="py-24 bg-card">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange">
              {t('international.title')}
            </h2>
            <p className="text-xl text-muted-foreground mb-12">
              {t('international.description')}
            </p>
            <ul className="space-y-4">
              {goalsArray.map((goal, index) => (
                <li key={index} className="text-lg text-muted-foreground">
                  • {goal}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Conclusion Section */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange">
              {t('conclusion.title')}
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              {t('conclusion.description')}
            </p>
            <p className="text-xl text-brand-orange mb-12">
              {t('conclusion.vision')}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 rounded-lg bg-gradient-to-r from-brand-blue to-brand-orange text-white hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl text-lg"
            >
              {t('conclusion.cta')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MetaRepository;
